import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SaveButton from '@/components/SaveButton';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Heart, 
  Briefcase, 
  Home, 
  ShoppingBag, 
  Building2, 
  Calendar,
  MapPin,
  DollarSign,
  Clock,
  Trash2,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { API, useAuth } from '@/App';

const SavedItemsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [savedItems, setSavedItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchSavedItems();
  }, [user]);

  const fetchSavedItems = async () => {
    try {
      const response = await axios.get(`${API}/saved-items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch saved items');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsave = async (itemType, itemId) => {
    try {
      await axios.post(
        `${API}/saved-items/unsave`,
        { item_type: itemType, item_id: itemId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Removed from saved');
      fetchSavedItems(); // Refresh the list
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const formatPrice = (price, currency = 'USD', period = '') => {
    if (!price) return 'Price not specified';
    const formatted = new Intl.NumberFormat('en-US').format(price);
    return `${currency} ${formatted}${period ? ` / ${period}` : ''}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Job Card Component
  const JobCard = ({ job }) => (
    <Card className="group hover:shadow-lg transition-all duration-300 border-slate-200 dark:border-slate-700">
      <CardContent className="p-5">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1 min-w-0">
            <Link to={`/jobs/${job.job_id}`} className="block">
              <h3 className="font-semibold text-lg text-slate-900 dark:text-white group-hover:text-teal-600 transition-colors truncate">
                {job.title}
              </h3>
            </Link>
            <p className="text-slate-600 dark:text-slate-400 flex items-center gap-1 mt-1">
              <Building2 className="h-4 w-4" />
              {job.company_name}
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {job.city}, {job.country}
              </span>
              <Badge variant="secondary" className="text-xs">
                {job.job_type}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {job.work_arrangement}
              </Badge>
            </div>
            {(job.salary_min || job.salary_max) && (
              <p className="text-teal-600 font-medium mt-2 flex items-center gap-1">
                <DollarSign className="h-4 w-4" />
                {formatPrice(job.salary_min, job.salary_currency)} - {formatPrice(job.salary_max, job.salary_currency)} / {job.salary_period}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleUnsave('job', job.job_id)}
            className="text-rose-500 hover:text-rose-600 hover:bg-rose-50"
            data-testid={`unsave-job-${job.job_id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Property Card Component
  const PropertyCard = ({ property }) => (
    <Card className="group hover:shadow-lg transition-all duration-300 border-slate-200 dark:border-slate-700">
      <CardContent className="p-5">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1 min-w-0">
            <Link to={`/housing/${property.property_id}`} className="block">
              <h3 className="font-semibold text-lg text-slate-900 dark:text-white group-hover:text-teal-600 transition-colors truncate">
                {property.title}
              </h3>
            </Link>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {property.district}, {property.city}
              </span>
              <Badge variant="secondary" className="text-xs">
                {property.property_type}
              </Badge>
              <Badge variant="outline" className="text-xs capitalize">
                {property.listing_type}
              </Badge>
            </div>
            <p className="text-teal-600 font-medium mt-2 flex items-center gap-1">
              <DollarSign className="h-4 w-4" />
              {formatPrice(property.price, property.price_currency, property.price_period)}
            </p>
            <div className="flex gap-3 mt-2 text-sm text-slate-500">
              {property.bedrooms !== undefined && <span>{property.bedrooms} bed</span>}
              {property.bathrooms !== undefined && <span>{property.bathrooms} bath</span>}
              {property.size_sqm && <span>{property.size_sqm} m²</span>}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleUnsave('property', property.property_id)}
            className="text-rose-500 hover:text-rose-600 hover:bg-rose-50"
            data-testid={`unsave-property-${property.property_id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Marketplace Listing Card Component
  const ListingCard = ({ listing }) => (
    <Card className="group hover:shadow-lg transition-all duration-300 border-slate-200 dark:border-slate-700">
      <CardContent className="p-5">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1 min-w-0">
            <Link to={`/marketplace/${listing.listing_id}`} className="block">
              <h3 className="font-semibold text-lg text-slate-900 dark:text-white group-hover:text-teal-600 transition-colors truncate">
                {listing.title}
              </h3>
            </Link>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {listing.district}, {listing.city}
              </span>
              <Badge variant="secondary" className="text-xs">
                {listing.category}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {listing.condition}
              </Badge>
            </div>
            <p className="text-teal-600 font-medium mt-2 flex items-center gap-1">
              {listing.is_free ? (
                <span className="text-green-600 font-bold">FREE</span>
              ) : (
                <>
                  <DollarSign className="h-4 w-4" />
                  {formatPrice(listing.price, listing.price_currency)}
                </>
              )}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleUnsave('listing', listing.listing_id)}
            className="text-rose-500 hover:text-rose-600 hover:bg-rose-50"
            data-testid={`unsave-listing-${listing.listing_id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Business Card Component
  const BusinessCard = ({ business }) => (
    <Card className="group hover:shadow-lg transition-all duration-300 border-slate-200 dark:border-slate-700">
      <CardContent className="p-5">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1 min-w-0">
            <Link to={`/businesses/${business.business_id}`} className="block">
              <h3 className="font-semibold text-lg text-slate-900 dark:text-white group-hover:text-teal-600 transition-colors truncate">
                {business.name}
              </h3>
            </Link>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {business.city}, {business.country}
              </span>
              <Badge variant="secondary" className="text-xs">
                {business.category}
              </Badge>
            </div>
            {business.description && (
              <p className="text-slate-600 dark:text-slate-400 mt-2 line-clamp-2 text-sm">
                {business.description}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleUnsave('business', business.business_id)}
            className="text-rose-500 hover:text-rose-600 hover:bg-rose-50"
            data-testid={`unsave-business-${business.business_id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Event Card Component
  const EventCard = ({ event }) => (
    <Card className="group hover:shadow-lg transition-all duration-300 border-slate-200 dark:border-slate-700">
      <CardContent className="p-5">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1 min-w-0">
            <Link to={`/events/${event.event_id}`} className="block">
              <h3 className="font-semibold text-lg text-slate-900 dark:text-white group-hover:text-teal-600 transition-colors truncate">
                {event.title}
              </h3>
            </Link>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {formatDate(event.date)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {event.time}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <MapPin className="h-3.5 w-3.5 text-slate-400" />
              <span className="text-sm text-slate-600">{event.venue}</span>
            </div>
            <Badge variant="secondary" className="mt-2 text-xs">
              {event.category}
            </Badge>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleUnsave('event', event.event_id)}
            className="text-rose-500 hover:text-rose-600 hover:bg-rose-50"
            data-testid={`unsave-event-${event.event_id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Empty state component
  const EmptyState = ({ icon: Icon, title, description }) => (
    <div className="text-center py-12">
      <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon className="h-8 w-8 text-slate-400" />
      </div>
      <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">{title}</h3>
      <p className="text-slate-500 max-w-sm mx-auto">{description}</p>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
        </div>
        <Footer />
      </div>
    );
  }

  const totalCount = savedItems?.counts?.total || 0;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="saved-items-page">
      <Navbar />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-rose-100 dark:bg-rose-900/30 rounded-lg">
              <Heart className="h-6 w-6 text-rose-500" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
              Saved Items
            </h1>
          </div>
          <p className="text-slate-600 dark:text-slate-400 mt-2">
            {totalCount === 0 
              ? "You haven't saved any items yet. Browse and save items that interest you!"
              : `You have ${totalCount} saved item${totalCount !== 1 ? 's' : ''}`
            }
          </p>
        </motion.div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 flex-wrap h-auto gap-2 bg-slate-100 dark:bg-slate-800 p-1.5 rounded-xl">
            <TabsTrigger 
              value="all" 
              className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 rounded-lg px-4"
              data-testid="tab-all"
            >
              All ({totalCount})
            </TabsTrigger>
            <TabsTrigger 
              value="jobs" 
              className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 rounded-lg px-4"
              data-testid="tab-jobs"
            >
              <Briefcase className="h-4 w-4 mr-2" />
              Jobs ({savedItems?.counts?.jobs || 0})
            </TabsTrigger>
            <TabsTrigger 
              value="properties" 
              className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 rounded-lg px-4"
              data-testid="tab-properties"
            >
              <Home className="h-4 w-4 mr-2" />
              Housing ({savedItems?.counts?.properties || 0})
            </TabsTrigger>
            <TabsTrigger 
              value="listings" 
              className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 rounded-lg px-4"
              data-testid="tab-listings"
            >
              <ShoppingBag className="h-4 w-4 mr-2" />
              Marketplace ({savedItems?.counts?.listings || 0})
            </TabsTrigger>
            <TabsTrigger 
              value="businesses" 
              className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 rounded-lg px-4"
              data-testid="tab-businesses"
            >
              <Building2 className="h-4 w-4 mr-2" />
              Businesses ({savedItems?.counts?.businesses || 0})
            </TabsTrigger>
            <TabsTrigger 
              value="events" 
              className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 rounded-lg px-4"
              data-testid="tab-events"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Events ({savedItems?.counts?.events || 0})
            </TabsTrigger>
          </TabsList>

          {/* All Items Tab */}
          <TabsContent value="all">
            {totalCount === 0 ? (
              <EmptyState 
                icon={Heart}
                title="No saved items yet"
                description="Browse jobs, housing, marketplace listings, businesses, and events to save items that interest you."
              />
            ) : (
              <div className="space-y-8">
                {savedItems?.saved_jobs?.length > 0 && (
                  <section>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                      <Briefcase className="h-5 w-5 text-teal-500" />
                      Saved Jobs
                    </h2>
                    <div className="grid gap-4">
                      {savedItems.saved_jobs.map(job => (
                        <JobCard key={job.job_id} job={job} />
                      ))}
                    </div>
                  </section>
                )}
                
                {savedItems?.saved_properties?.length > 0 && (
                  <section>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                      <Home className="h-5 w-5 text-teal-500" />
                      Saved Properties
                    </h2>
                    <div className="grid gap-4">
                      {savedItems.saved_properties.map(property => (
                        <PropertyCard key={property.property_id} property={property} />
                      ))}
                    </div>
                  </section>
                )}
                
                {savedItems?.saved_listings?.length > 0 && (
                  <section>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                      <ShoppingBag className="h-5 w-5 text-teal-500" />
                      Saved Marketplace Listings
                    </h2>
                    <div className="grid gap-4">
                      {savedItems.saved_listings.map(listing => (
                        <ListingCard key={listing.listing_id} listing={listing} />
                      ))}
                    </div>
                  </section>
                )}
                
                {savedItems?.saved_businesses?.length > 0 && (
                  <section>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-teal-500" />
                      Saved Businesses
                    </h2>
                    <div className="grid gap-4">
                      {savedItems.saved_businesses.map(business => (
                        <BusinessCard key={business.business_id} business={business} />
                      ))}
                    </div>
                  </section>
                )}
                
                {savedItems?.saved_events?.length > 0 && (
                  <section>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-teal-500" />
                      Saved Events
                    </h2>
                    <div className="grid gap-4">
                      {savedItems.saved_events.map(event => (
                        <EventCard key={event.event_id} event={event} />
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </TabsContent>

          {/* Individual Category Tabs */}
          <TabsContent value="jobs">
            {savedItems?.saved_jobs?.length === 0 ? (
              <EmptyState 
                icon={Briefcase}
                title="No saved jobs"
                description="Browse the job board and save opportunities that interest you."
              />
            ) : (
              <div className="grid gap-4">
                {savedItems?.saved_jobs?.map(job => (
                  <JobCard key={job.job_id} job={job} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="properties">
            {savedItems?.saved_properties?.length === 0 ? (
              <EmptyState 
                icon={Home}
                title="No saved properties"
                description="Browse housing listings and save properties that catch your eye."
              />
            ) : (
              <div className="grid gap-4">
                {savedItems?.saved_properties?.map(property => (
                  <PropertyCard key={property.property_id} property={property} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="listings">
            {savedItems?.saved_listings?.length === 0 ? (
              <EmptyState 
                icon={ShoppingBag}
                title="No saved marketplace items"
                description="Browse the marketplace and save items you're interested in."
              />
            ) : (
              <div className="grid gap-4">
                {savedItems?.saved_listings?.map(listing => (
                  <ListingCard key={listing.listing_id} listing={listing} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="businesses">
            {savedItems?.saved_businesses?.length === 0 ? (
              <EmptyState 
                icon={Building2}
                title="No saved businesses"
                description="Browse the business directory and save places you want to remember."
              />
            ) : (
              <div className="grid gap-4">
                {savedItems?.saved_businesses?.map(business => (
                  <BusinessCard key={business.business_id} business={business} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="events">
            {savedItems?.saved_events?.length === 0 ? (
              <EmptyState 
                icon={Calendar}
                title="No saved events"
                description="Browse upcoming events and save ones you'd like to attend."
              />
            ) : (
              <div className="grid gap-4">
                {savedItems?.saved_events?.map(event => (
                  <EventCard key={event.event_id} event={event} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <Footer />
    </div>
  );
};

export default SavedItemsPage;
